import { AlertCircle } from "@untitledui/icons";
import type { FC, ReactNode } from "react";
import { Heading as AriaHeading } from "react-aria-components";
import { Dialog, Modal, ModalOverlay } from "@admin-ui/components/primitives/application/modals/modal";
import { Button } from "../../base/buttons/button";
import { CloseButton } from "../../base/buttons/close-button";
import { FeaturedIcon } from "../../foundations/featured-icon";

export interface ConfirmationDialogProps {
  title: string;
  description: string | ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  icon?: FC<{ className?: string }>;
  color?: "gray" | "warning" | "error";
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onConfirm: () => void;
}

export function ConfirmationDialog({
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  icon = AlertCircle,
  color = "gray",
  isOpen,
  onOpenChange,
  onConfirm,
}: ConfirmationDialogProps) {
  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable>
      <Modal>
        <Dialog>
          {({ close }) => (
            <div className="pointer-events-auto relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-100">
              <CloseButton
                onPress={close}
                theme="light"
                size="sm"
                className="absolute top-3 right-3 sm:top-4 sm:right-4"
              />
              <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                <div className="relative w-max">
                  <FeaturedIcon color={color} size="md" theme="modern" icon={icon} />
                </div>
                <div className="z-10 flex flex-col gap-0.5">
                  <AriaHeading slot="title" className="text-md font-semibold text-primary">
                    {title}
                  </AriaHeading>
                  <p className="text-sm text-tertiary">{description}</p>
                </div>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="md" onClick={close}>
                  {cancelLabel}
                </Button>
                <Button
                  color="primary"
                  size="md"
                  onClick={() => {
                    onConfirm();
                    close();
                  }}
                >
                  {confirmLabel}
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
